import { GoogleGenAI, Type } from "@google/genai";
import { createClient } from "@supabase/supabase-js";
import { BirthData, ChartResult, CompatibilityResult, BookingState, MemberRecord, BlogPost, Message, Bond, ExclusiveContent, BookingRecord, LocationSuggestion, VertexFortuneResult, Language } from "../types";

const resolveGeminiApiKey = (): string | undefined => {
  if (typeof import.meta !== "undefined" && import.meta.env?.VITE_GEMINI_API_KEY) {
    return import.meta.env.VITE_GEMINI_API_KEY;
  }

  const globalObj = globalThis as typeof globalThis & {
    process?: { env?: Record<string, string> };
  };
  return globalObj.process?.env?.API_KEY;
};

let aiClient: GoogleGenAI | null = null;

const getGeminiClient = () => {
  if (aiClient) return aiClient;
  const apiKey = resolveGeminiApiKey();
  if (!apiKey) {
    throw new Error("API_KEY missing. Set VITE_GEMINI_API_KEY to enable Gemini.");
  }
  aiClient = new GoogleGenAI({ apiKey });
  return aiClient;
};

const supabaseUrl = "https://ewtjrctxwdbrzppnuixe.supabase.co";
const supabaseAnonKey = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImV3dGpyY3R4d2RicnpwcG51aXhlIiwicm9sZSI6ImFub24iLCJpYXQiOjE3Njk5ODAzMjMsImV4cCI6MjA4NTU1NjMyM30.avz7k-jrVGNyIvCk1D5ivvAeBiT7zllpu7cHJgtB9GM";

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

const ADMIN_EMAIL = "allie.pakele@gmail.com";

// Helper to sanitize JSON response from Gemini
const parseGeminiJson = (text: string | undefined) => {
  if (!text) return {};
  try {
    // Remove markdown code blocks if present
    const cleanText = text.replace(/```json/g, '').replace(/```/g, '').trim();
    return JSON.parse(cleanText);
  } catch (e) {
    console.error("Failed to parse Gemini JSON:", text);
    throw new Error("The celestial response was malformed.");
  }
};

export const notifyAdmin = async (subject: string, data: any) => {
  try {
    const response = await fetch("https://formspree.io/f/mqakevve", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json"
      },
      body: JSON.stringify({
        _subject: `[Celestial Hub] ${subject}`,
        _to: ADMIN_EMAIL,
        message: `A new event has occurred in the celestial portal for Allie.`,
        timestamp: new Date().toLocaleString(),
        ...data
      })
    });
    return response.ok;
  } catch (error) {
    console.error("Celestial signal failed:", error);
    return false;
  }
};

export const getAdminStatus = async (): Promise<'online' | 'away'> => {
  try {
    const { data, error } = await supabase.from('admin_presence').select('status').eq('id', 'allie_main').maybeSingle();
    if (error || !data) return 'away';
    return (data as any).status as 'online' | 'away';
  } catch (e) {
    return 'away';
  }
};

export const setAdminStatus = async (status: 'online' | 'away') => {
  const { error } = await supabase.from('admin_presence').upsert({ 
    id: 'allie_main',
    status: status, 
    last_updated: new Date().toISOString() 
  }, { onConflict: 'id' });
  if (error) throw error;
};

export const deductCredits = async (memberId: string, type: 'messaging' | 'video' | 'voice', amount: number = 1) => {
  if (memberId === 'demo-member') return { success: true };
  const fieldMap: Record<string, string> = { 'messaging': 'messaging_credits', 'video': 'video_credits', 'voice': 'voice_credits' };
  const field = fieldMap[type];
  const { data: memberData, error } = await supabase.from('memberships').select('*').eq('id', memberId).single();
  
  if (error || !memberData) throw new Error("Member not found");
  
  const member = memberData as any;
  const currentVal = (member[field] as number) || 0;
  
  if (currentVal < amount) return { success: false, error: 'Insufficient credits' };
  
  const { data, error: updateError } = await supabase.from('memberships')
    .update({ [field]: Math.max(0, currentVal - amount) })
    .eq('id', memberId)
    .select()
    .single();
    
  if (updateError) throw updateError;
  return { success: true, member: data };
};

export const calculateBirthChart = async (data: BirthData, lang: Language = 'en'): Promise<ChartResult> => {
  try {
    const coordString = data.lat !== undefined && data.lng !== undefined 
      ? `STRICT GEOGRAPHIC COORDINATES: Latitude ${data.lat}, Longitude ${data.lng}.`
      : `General Location: ${data.location}.`;

    // gemini-3-pro-preview requires a non-zero thinking budget when in thinking mode.
    // Setting 2000 tokens for accurate astronomical arithmetic.
    const response = await getGeminiClient().models.generateContent({
      model: 'gemini-3-pro-preview', 
      contents: `Calculate a high-precision natal chart interpretation for: ${data.name}, born ${data.date} at ${data.time}. 
      ${coordString}
      
      SCIENTIFIC PROTOCOL:
      1. Use UTC offsets for the specific date.
      2. Calculate planetary degrees (Sun-Pluto) with arc-minute precision.
      3. Use Placidus House System.
      4. RESPONSE LANGUAGE: ${lang}.
      
      Return JSON only.`,
      config: {
        thinkingConfig: { thinkingBudget: 2000 },
        responseMimeType: "application/json",
        responseSchema: {
          type: Type.OBJECT,
          properties: {
            sunSign: { type: Type.STRING },
            moonSign: { type: Type.STRING },
            risingSign: { type: Type.STRING },
            summary: { type: Type.STRING },
            coordinates: {
              type: Type.OBJECT,
              properties: {
                lat: { type: Type.NUMBER },
                lng: { type: Type.NUMBER }
              },
              required: ["lat", "lng"]
            },
            placements: { 
              type: Type.ARRAY, 
              items: { 
                type: Type.OBJECT, 
                properties: { 
                  planet: { type: Type.STRING }, 
                  sign: { type: Type.STRING }, 
                  degree: { type: Type.INTEGER }, 
                  minutes: { type: Type.INTEGER }, 
                  seconds: { type: Type.INTEGER },
                  house: { type: Type.INTEGER }, 
                  description: { type: Type.STRING } 
                },
                required: ["planet", "sign", "degree", "minutes", "seconds", "house", "description"]
              } 
            },
            houses: { 
              type: Type.ARRAY, 
              items: { 
                type: Type.OBJECT, 
                properties: { 
                  houseNumber: { type: Type.INTEGER }, 
                  sign: { type: Type.STRING }, 
                  degree: { type: Type.INTEGER }, 
                  minutes: { type: Type.INTEGER }, 
                  seconds: { type: Type.INTEGER },
                  description: { type: Type.STRING } 
                },
                required: ["houseNumber", "sign", "degree", "minutes", "seconds", "description"]
              } 
            }
          },
          required: ["sunSign", "moonSign", "risingSign", "summary", "coordinates", "placements", "houses"]
        }
      }
    });

    const chartData = parseGeminiJson(response.text) as ChartResult;
    chartData.sources = [
      { title: 'Allie\'s High-Speed Ephemeris Engine', uri: '#' },
      { title: 'Gemini 3 Pro Precision Logic', uri: '#' }
    ];

    return chartData;
  } catch (error) {
    console.error("CELESTIAL CALCULATION ERROR:", error);
    throw error;
  }
};

export const calculateVertexAndFortune = async (data: BirthData, lang: Language = 'en'): Promise<VertexFortuneResult> => {
  try {
    const response = await getGeminiClient().models.generateContent({
      model: 'gemini-3-pro-preview',
      contents: `Calculate Vertex and Part of Fortune for: ${data.name}, born ${data.date} at ${data.time} in ${data.location}. 
      RESPONSE MUST BE IN LANGUAGE: ${lang}. Return JSON only.`,
      config: {
        thinkingConfig: { thinkingBudget: 2000 },
        responseMimeType: "application/json",
        responseSchema: {
          type: Type.OBJECT,
          properties: {
            vertex: {
              type: Type.OBJECT,
              properties: {
                sign: { type: Type.STRING },
                degree: { type: Type.NUMBER },
                house: { type: Type.NUMBER },
                meaning: { type: Type.STRING },
                fated_lessons: { type: Type.STRING },
                relationship_impact: { type: Type.STRING },
                karmic_trigger: { type: Type.STRING }
              }
            },
            part_of_fortune: {
              type: Type.OBJECT,
              properties: {
                sign: { type: Type.STRING },
                degree: { type: Type.NUMBER },
                house: { type: Type.NUMBER },
                meaning: { type: Type.STRING },
                abundance_path: { type: Type.STRING },
                hidden_talents: { type: Type.STRING },
                best_day_for_fortune: { type: Type.STRING }
              }
            },
            critical_degrees: {
              type: Type.ARRAY,
              items: {
                type: Type.OBJECT,
                properties: {
                  planet: { type: Type.STRING },
                  degree: { type: Type.NUMBER },
                  sign: { type: Type.STRING },
                  meaning: { type: Type.STRING },
                  power: { type: Type.STRING }
                }
              }
            },
            synergy_summary: { type: Type.STRING }
          },
          required: ["vertex", "part_of_fortune", "critical_degrees", "synergy_summary"]
        }
      }
    });
    return parseGeminiJson(response.text) as VertexFortuneResult;
  } catch (error) {
    console.error("Vertex Calculation Failed:", error);
    throw error;
  }
};

export const calculateCompatibility = async (person1: BirthData, person2: BirthData, lang: Language = 'en'): Promise<CompatibilityResult> => {
  try {
    const p1Coord = person1.lat ? `(Lat: ${person1.lat}, Lng: ${person1.lng})` : '';
    const p2Coord = person2.lat ? `(Lat: ${person2.lat}, Lng: ${person2.lng})` : '';
    
    const response = await getGeminiClient().models.generateContent({
      model: 'gemini-3-flash-preview',
      contents: `Analyze synastry compatibility between ${person1.name} ${p1Coord} and ${person2.name} ${p2Coord}. RESPONSE MUST BE IN LANGUAGE: ${lang}. Return JSON only.`,
      config: {
        responseMimeType: "application/json",
        responseSchema: {
          type: Type.OBJECT,
          properties: {
            score: { type: Type.INTEGER },
            harmonySummary: { type: Type.STRING },
            emotionalConnection: { type: Type.STRING },
            intellectualConnection: { type: Type.STRING },
            physicalConnection: { type: Type.STRING },
            challenges: { type: Type.STRING }
          }
        }
      }
    });
    return parseGeminiJson(response.text) as CompatibilityResult;
  } catch (error) { throw error; }
};

export const addBond = async (memberId: string, bondData: Omit<Bond, 'id' | 'created_at'>) => {
  const newBond: Bond = { ...bondData, id: crypto.randomUUID(), created_at: new Date().toISOString() };
  if (memberId === 'demo-member') return { success: true, bond: newBond };
  const { data: member } = await supabase.from('memberships').select('bonds, name').eq('id', memberId).single();
  const currentBonds = (member as any)?.bonds || [];
  const { error } = await supabase.from('memberships').update({ bonds: [newBond, ...currentBonds] }).eq('id', memberId);
  if (error) throw error;
  await notifyAdmin("New Relationship Bond Created", { member: (member as any)?.name, bond_name: newBond.name });
  return { success: true, bond: newBond };
};

export const updateBond = async (memberId: string, bondId: string, bondData: Partial<Bond>) => {
  if (memberId === 'demo-member') return { success: true };
  const { data: member } = await supabase.from('memberships').select('bonds').eq('id', memberId).single();
  const currentBonds = (member as any)?.bonds || [];
  const updatedBonds = currentBonds.map((b: Bond) => b.id === bondId ? { ...b, ...bondData } : b);
  const { error } = await supabase.from('memberships').update({ bonds: updatedBonds }).eq('id', memberId);
  if (error) throw error;
  return { success: true };
};

export const deleteBond = async (memberId: string, bondId: string) => {
  const { data: member } = await supabase.from('memberships').select('bonds').eq('id', memberId).single();
  const filteredBonds = ((member as any)?.bonds || []).filter((b: Bond) => b.id !== bondId);
  const { error } = await supabase.from('memberships').update({ bonds: filteredBonds }).eq('id', memberId);
  if (error) throw error;
  return { success: true };
};

export const suggestLocations = async (query: string): Promise<LocationSuggestion[]> => {
  if (query.length < 3) return [];
  try {
    const response = await getGeminiClient().models.generateContent({
      model: 'gemini-3-flash-preview',
      contents: `Identify 5 real-world cities matching: "${query}". Return a JSON array with name, lat, lng.`,
      config: { 
        responseMimeType: "application/json",
        responseSchema: {
          type: Type.ARRAY,
          items: {
            type: Type.OBJECT,
            properties: {
              name: { type: Type.STRING },
              lat: { type: Type.NUMBER },
              lng: { type: Type.NUMBER }
            },
            required: ["name", "lat", "lng"]
          }
        }
      }
    });
    return parseGeminiJson(response.text) as LocationSuggestion[];
  } catch (error) { return []; }
};

export const findMemberByEmail = async (email: string): Promise<MemberRecord | null> => {
  const { data, error } = await supabase.from('memberships').select('*').ilike('email', email.trim()).maybeSingle();
  if (error) return null;
  return data as MemberRecord;
};

export const updateMemberProfile = async (id: string, updates: Partial<MemberRecord>) => {
  const { error } = await supabase.from('memberships').update(updates).eq('id', id);
  if (error) throw error;
  return { success: true };
};

export const updateMemberTier = async (id: string, newTier: string) => {
  const credits = newTier === 'Lunar Seeker' ? 0 : newTier === 'Solar Initiate' ? 5 : 10;
  return await updateMemberProfile(id, { tier: newTier, messaging_credits: credits, video_credits: credits, voice_credits: credits });
};

export const addCredits = async (id: string, type: 'messaging' | 'video' | 'voice', minutes: number) => {
  const { data: memberData } = await supabase.from('memberships').select('*').eq('id', id).single();
  const fieldMap: Record<string, string> = { 'messaging': 'messaging_credits', 'video': 'video_credits', 'voice': 'voice_credits' };
  const field = fieldMap[type];
  const member = memberData as any;
  const newValue = (member[field] || 0) + minutes;
  const { data, error } = await supabase.from('memberships').update({ [field]: newValue }).eq('id', id).select().single();
  if (error) throw error;
  return { success: true, member: data };
};

export const fetchMemberships = async () => {
  const { data, error } = await supabase.from('memberships').select('*').order('joined_at', { ascending: false });
  if (error) throw error;
  return data || [];
};

export const fetchMemberBookings = async (email: string): Promise<BookingRecord[]> => {
  const { data, error } = await supabase.from('bookings').select('*').eq('email', email).order('appointment_date', { ascending: false });
  if (error) throw error;
  return data || [];
};

export const fetchAllBookings = async (): Promise<BookingRecord[]> => {
  const { data, error } = await supabase.from('bookings').select('*').order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
};

export const submitBooking = async (booking: BookingState, paymentDetails: any) => {
  const { error } = await supabase.from('bookings').insert([{
    name: booking.name,
    email: booking.email,
    phone: booking.phone,
    service_ids: booking.serviceIds,
    commMethod: booking.commMethod,
    appointment_date: booking.date ? booking.date.toISOString().split('T')[0] : null,
    appointment_time: booking.time,
    payment_id: paymentDetails.id,
    payment_amount: paymentDetails.purchase_units?.[0]?.amount?.value || 0,
    status: 'paid',
    created_at: new Date().toISOString()
  }]);
  if (error) throw error;
  await notifyAdmin("New Session Alignment Secured", { seeker: booking.name });
  return { success: true };
};

export const fetchBlogs = async (): Promise<BlogPost[]> => {
  const { data, error } = await supabase.from('blogs').select('*').order('date', { ascending: false });
  return (data || []) as BlogPost[];
};

export const subscribeUser = async (name: string, email: string) => {
  const { error } = await supabase.from('subscribers').insert([{ name, email, subscription_date: new Date().toISOString() }]);
  if (error) throw error;
  return { success: true };
};

export const joinMembership = async (memberData: any): Promise<{ success: boolean; member?: MemberRecord }> => {
  const credits = memberData.tier === 'Lunar Seeker' ? 0 : memberData.tier === 'Solar Initiate' ? 5 : 10;
  const { data, error } = await supabase.from('memberships').insert([{
    ...memberData,
    location: memberData.birth_location,
    joined_at: new Date().toISOString(),
    messaging_credits: credits, video_credits: credits, voice_credits: credits,
    bonds: [], unlocked_contents: []
  }]).select().single();
  if (error) throw error;
  await notifyAdmin("New Sacred Soul Membership", { name: memberData.name, email: memberData.email });
  return { success: true, member: data as MemberRecord };
};

export const fetchMessages = async (userId: string): Promise<Message[]> => {
  const { data, error } = await supabase.from('messages').select('*').or(`sender_id.eq.${userId},receiver_id.eq.${userId}`).order('created_at', { ascending: true });
  return (data || []) as Message[];
};

export const fetchAllMessagesForAdmin = async (): Promise<Message[]> => {
  try {
    const { data, error } = await supabase.from('messages').select('*').order('created_at', { ascending: true });
    if (error) throw error;
    return (data || []) as Message[];
  } catch (e) { return []; }
};

export const sendMessage = async (message: Omit<Message, 'id' | 'created_at'>) => {
  const { data, error } = await supabase.from('messages').insert([{ ...message, created_at: new Date().toISOString() }]);
  if (error) throw error;
  return data;
};

export const getCelestialResponse = async (message: string, lang: Language = 'en'): Promise<string> => {
  try {
    const response = await getGeminiClient().models.generateContent({
      model: 'gemini-3-flash-preview',
      contents: message,
      config: {
        systemInstruction: `You are the Jupiter Jewels Celestial Guide. Professional, helpful, mystical. Answer about zodiac and Allie's services. Use Astro-Seek levels of accuracy. RESPONSE MUST BE IN LANGUAGE: ${lang}.`,
      }
    });
    return response.text || "The stars are silent.";
  } catch (error) { return "Cosmic frequency distorted."; }
};

export const getPersonalizedHoroscope = async (data: BirthData, type: 'daily' | 'weekly' | 'yearly', lang: Language = 'en'): Promise<string> => {
  try {
    const response = await getGeminiClient().models.generateContent({
      model: 'gemini-3-flash-preview',
      contents: `Short horoscope for ${data.name} (Born: ${data.date}) - ${type}. Use Astro-Seek precision. RESPONSE MUST BE IN LANGUAGE: ${lang}.`,
      config: {
        systemInstruction: "Wise friend style. 2 sentences max. No jargon.",
      }
    });
    return response.text || "The stars shift.";
  } catch (error) { return "Unaligned."; }
};

export const startLiveSession = async (bookingId: string) => {
  const startTime = new Date().toISOString();
  await supabase.from('bookings').update({ session_active: true, session_start_time: startTime }).eq('id', bookingId);
  return startTime;
};

export const endLiveSession = async (bookingId: string) => {
  await supabase.from('bookings').update({ session_active: false, status: 'completed' }).eq('id', bookingId);
};

export const fetchExclusiveContent = async (): Promise<ExclusiveContent[]> => {
  const { data, error } = await supabase.from('exclusive_content').select('*').order('created_at', { ascending: false });
  return (data || []) as ExclusiveContent[];
};

export const createExclusiveContent = async (content: Omit<ExclusiveContent, 'id' | 'created_at'>) => {
  await supabase.from('exclusive_content').insert([{ ...content, created_at: new Date().toISOString() }]);
  return { success: true };
};

export const unlockContent = async (memberId: string, contentId: string) => {
  const { data: member } = await supabase.from('memberships').select('unlocked_contents').eq('id', memberId).single();
  const current = (member as any)?.unlocked_contents || [];
  await supabase.from('memberships').update({ unlocked_contents: [...current, contentId] }).eq('id', memberId);
  return { success: true };
};

export const fetchSessionLogs = async (memberId: string) => {
  const { data, error } = await supabase.from('session_logs').select('*').eq('member_id', memberId).order('timestamp', { ascending: false });
  return data || [];
};

export const saveSessionLog = async (log: any) => {
  await supabase.from('session_logs').insert([{ ...log, timestamp: new Date().toISOString() }]);
  return { success: true };
};
